import {useState, useCallback, useEffect} from "react";
import {isAxiosError} from "axios";
import { APIError } from "@/api/APIError";

type GolemErrors = Record<string, string[]>;

export type GolemFieldError = { path: string; message: string };

export type GolemErrorState = {
    fieldError: (field: string) => string | undefined;
    fieldChildErrors: (field: string) => GolemFieldError[];
    clearField: (field: string) => void;
    setFromObject: (obj: Record<string, string[]>) => void;
}

export function useGolemErrors() {
    const [errors, setErrors] = useState<GolemErrors>({});

    useEffect(() => {
        if (Object.keys(errors).length === 0) return;
        const id = setTimeout(() => {
            const el = document.querySelector("[data-field-error]");
            if (el) el.scrollIntoView({behavior: "smooth", block: "center"});
        }, 0);
        return () => clearTimeout(id);
    }, [errors]);

    const setFromError = useCallback((e: unknown) => {
        if (e instanceof APIError && e.errors) {
            setErrors(e.errors as GolemErrors);
        } else if (isAxiosError(e) && e.response?.data?.errors) {
            setErrors(e.response.data.errors as GolemErrors);
        } else {
            setErrors({});
        }
    }, []);

    const setFromAxiosError = setFromError;

    const clearErrors = useCallback(() => setErrors({}), []);

    const setFromObject = useCallback((obj: Record<string, string[]>) => {
        setErrors(obj);
    }, []);

    const fieldError = useCallback((field: string): string | undefined => {
        if (errors[field]?.[0]) return errors[field][0];
        const prefix = field + ".";
        for (const key of Object.keys(errors)) {
            if (key.startsWith(prefix) && errors[key]?.[0]) return errors[key][0];
        }
        return undefined;
    }, [errors]);

    const fieldChildErrors = useCallback((field: string): GolemFieldError[] => {
        const prefix = field + ".";
        const result: GolemFieldError[] = [];
        for (const key of Object.keys(errors)) {
            if (key.startsWith(prefix) && errors[key]?.[0]) {
                const path = key.slice(prefix.length).replace(/\.(\d+)/g, "[$1]");
                result.push({ path, message: errors[key][0] });
            }
        }
        return result;
    }, [errors]);

    const clearField = useCallback((field: string) => {
        setErrors(prev => {
            const prefix = field + ".";
            const keys = Object.keys(prev).filter(k => k === field || k.startsWith(prefix));
            if (keys.length === 0) return prev;
            const next = {...prev};
            for (const k of keys) delete next[k];
            return next;
        });
    }, []);

    return {fieldError, fieldChildErrors, clearField, setFromAxiosError, setFromError, clearErrors, setFromObject};
}
